import React from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import { TrustBadges } from '../components/TrustBadges';
const plans = [{
  name: 'Silver Plan',
  price: '£99',
  ebooks: 'Up to 200 ebooks',
  period: 'Annual subscription',
  image: 'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?auto=format&fit=crop&q=80&w=800',
  features: ['Access to 200 curated titles', 'Read on any device', 'Offline reading mode', 'Standard support'],
  stripeLink: 'https://buy.stripe.com/4gM00jbrv27CgQM7Y86kg00',
  popular: false,
  borderColor: 'border-slate-200'
}, {
  name: 'Gold Plan',
  price: '£120',
  ebooks: 'Up to 700 ebooks',
  period: 'Annual subscription',
  image: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&q=80&w=800',
  features: ['Access to 700 curated titles', 'Read on any device', 'Offline reading mode', 'Priority email support', 'Early access to new releases'],
  stripeLink: 'https://buy.stripe.com/28E3cvdzD9A4fMI92c6kg01',
  popular: true,
  borderColor: 'border-accent'
}, {
  name: 'Platinum Plan',
  price: '£150',
  ebooks: 'Unlimited ebooks',
  period: 'Annual subscription',
  image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&q=80&w=800',
  features: ['Unlimited access to full library', 'Read on any device', 'Offline reading mode', '24/7 Premium support', 'Early access to new releases', 'Family sharing (up to 3 users)'],
  stripeLink: 'https://buy.stripe.com/28E28r537fYs6c80vG6kg02',
  popular: false,
  borderColor: 'border-slate-200'
}];
const comparisonFeatures = [{
  name: 'Number of Ebooks',
  silver: '200',
  gold: '700',
  platinum: 'Unlimited'
}, {
  name: 'Annual Price',
  silver: '£99',
  gold: '£120',
  platinum: '£150'
}, {
  name: 'Subscription Type',
  silver: 'Annual',
  gold: 'Annual',
  platinum: 'Annual'
}, {
  name: 'Instant Access',
  silver: true,
  gold: true,
  platinum: true
}, {
  name: 'Multi-device Support',
  silver: true,
  gold: true,
  platinum: true
}, {
  name: 'Offline Reading',
  silver: true,
  gold: true,
  platinum: true
}, {
  name: 'Customer Support',
  silver: 'Standard',
  gold: 'Priority',
  platinum: '24/7 Premium'
}, {
  name: 'Family Sharing',
  silver: false,
  gold: false,
  platinum: true
}];
export function ServiceDetailsPage() {
  return <main className="flex-1 bg-background" data-id="element-399">
      {/* Hero Section */}
      <section className="relative pt-16 pb-12 lg:pt-24 lg:pb-16 bg-gradient-to-br from-primary/5 to-accent/5" data-id="element-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-401">
          <motion.h1 initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-4xl md:text-5xl font-bold text-slate-dark mb-6" data-id="element-402">
            Choose Your Plan
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed" data-id="element-403">
            Bluecore offers annual subscription plans that provide access to a
            set number of ebooks per year. Select the plan that best fits your
            reading needs.
          </motion.p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-white" data-id="element-404">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-405">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-406">
            {plans.map((plan, index) => <motion.div key={plan.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.15,
            duration: 0.5
          }} className={`flex flex-col bg-white rounded-xl shadow-sm border ${plan.borderColor} overflow-hidden relative group hover:shadow-lg transition-all duration-300`} data-id="element-407">
                {plan.popular && <div className="absolute top-4 right-4 z-10 bg-accent text-white px-3 py-1 rounded-full text-sm font-semibold shadow-sm" data-id="element-408">
                    Most Popular
                  </div>}

                {/* Card Image */}
                <div className="h-48 overflow-hidden relative" data-id="element-409">
                  <img src={plan.image} alt={`${plan.name} visual`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-410" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" data-id="element-411" />
                  <div className="absolute bottom-4 left-4 text-white font-bold text-lg drop-shadow-md" data-id="element-412">
                    {plan.ebooks}
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-8 flex-1 flex flex-col" data-id="element-413">
                  <h3 className="text-2xl font-bold text-slate-dark mb-2" data-id="element-414">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-415">
                    <span className="text-4xl font-bold text-primary" data-id="element-416">
                      {plan.price}
                    </span>
                    <span className="text-slate-500 text-sm" data-id="element-417">/ year</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-6 font-medium bg-slate-50 inline-block py-1 px-2 rounded self-start" data-id="element-418">
                    {plan.period}
                  </p>

                  <ul className="space-y-4 mb-8 flex-1" data-id="element-419">
                    {plan.features.map(feature => <li key={feature} className="flex items-start gap-3 text-slate-600" data-id="element-420">
                        <Check className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" data-id="element-421" />
                        <span className="text-sm" data-id="element-422">{feature}</span>
                      </li>)}
                  </ul>

                  <a href={plan.stripeLink} className={`w-full block text-center py-4 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg active:transform active:scale-95 ${plan.popular ? 'bg-accent hover:bg-accent-dark text-white' : 'bg-slate-800 hover:bg-slate-700 text-white'}`} data-id="element-423">
                    Buy Now
                  </a>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-slate-50" data-id="element-424">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-425">
          <div className="text-center mb-12" data-id="element-426">
            <h2 className="text-3xl font-bold text-slate-dark mb-4" data-id="element-427">
              Compare Plans
            </h2>
            <p className="text-slate-600" data-id="element-428">
              Detailed breakdown of features and benefits
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-429">
            {/* Desktop Table Header */}
            <div className="hidden md:grid grid-cols-4 bg-slate-100 p-6 border-b border-slate-200 font-bold text-slate-dark" data-id="element-430">
              <div className="col-span-1" data-id="element-431">Features</div>
              <div className="text-center" data-id="element-432">Silver</div>
              <div className="text-center text-accent" data-id="element-433">Gold</div>
              <div className="text-center" data-id="element-434">Platinum</div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-slate-100" data-id="element-435">
              {comparisonFeatures.map((row, index) => <div key={row.name} className={`grid grid-cols-1 md:grid-cols-4 p-6 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-slate-50 transition-colors`} data-id="element-436">
                  <div className="font-medium text-slate-700 mb-2 md:mb-0 flex items-center gap-2" data-id="element-437">
                    {row.name}
                  </div>

                  {/* Mobile labels included for responsive stacking */}
                  <div className="grid grid-cols-3 md:contents gap-4" data-id="element-438">
                    <div className="flex flex-col md:items-center justify-center" data-id="element-439">
                      <span className="md:hidden text-xs text-slate-400 mb-1 font-bold uppercase" data-id="element-440">
                        Silver
                      </span>
                      <span className="text-slate-600 font-medium text-center" data-id="element-441">
                        {typeof row.silver === 'boolean' ? row.silver ? <Check className="w-5 h-5 text-green-500 mx-auto" data-id="element-442" /> : <span className="text-slate-300" data-id="element-443">-</span> : row.silver}
                      </span>
                    </div>

                    <div className="flex flex-col md:items-center justify-center bg-accent/5 md:bg-transparent rounded p-2 md:p-0" data-id="element-444">
                      <span className="md:hidden text-xs text-accent mb-1 font-bold uppercase" data-id="element-445">
                        Gold
                      </span>
                      <span className="text-slate-800 font-bold text-center" data-id="element-446">
                        {typeof row.gold === 'boolean' ? row.gold ? <Check className="w-5 h-5 text-accent mx-auto" data-id="element-447" /> : <span className="text-slate-300" data-id="element-448">-</span> : row.gold}
                      </span>
                    </div>

                    <div className="flex flex-col md:items-center justify-center" data-id="element-449">
                      <span className="md:hidden text-xs text-slate-400 mb-1 font-bold uppercase" data-id="element-450">
                        Platinum
                      </span>
                      <span className="text-slate-600 font-medium text-center" data-id="element-451">
                        {typeof row.platinum === 'boolean' ? row.platinum ? <Check className="w-5 h-5 text-green-500 mx-auto" data-id="element-452" /> : <span className="text-slate-300" data-id="element-453">-</span> : row.platinum}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-16 bg-white border-t border-slate-100" data-id="element-454">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-455">
          <h2 className="text-2xl font-bold text-slate-dark mb-8" data-id="element-456">
            Why Choose Bluecore?
          </h2>
          <TrustBadges data-id="element-457" />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-458">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1519681393798-3828fb4090bb?auto=format&fit=crop&q=80&w=2000')] bg-cover bg-center opacity-10" data-id="element-459" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-slate-900/80" data-id="element-460" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-461">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-462">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto" data-id="element-463">
            Choose your plan and start reading today. Cancel anytime.
          </p>
          <button onClick={() => window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })} className="bg-accent hover:bg-accent-dark text-white px-10 py-4 rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-accent/20 active:transform active:scale-95 flex items-center justify-center gap-2 mx-auto" data-id="element-464">
            View All Plans
            <ArrowRight className="w-5 h-5" data-id="element-465" />
          </button>
        </div>
      </section>
    </main>;
}